import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../../assets/assetKeys';
import { ZONE2_POSITIONS } from './Zone2Map';

export class Zone2AnimalEvents {
  private scene: Phaser.Scene;
  private playing = false;
  private played = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  hasPlayed(): boolean {
    return this.played;
  }

  play(onComplete: () => void): void {
    if (this.played) {
      onComplete();
      return;
    }
    if (this.playing) return;

    this.playing = true;

    const monkeyGroundY = ZONE2_POSITIONS.ANIMAL_TRIGGER.y;
    const tigerGroundY = ZONE2_POSITIONS.BUSH_TIGER.y;
    const eventObjects: Phaser.GameObjects.GameObject[] = [];

    const makeDust = (x: number, y: number, delay: number) => {
      const dust = this.scene.add.ellipse(x, y, 26, 12, 0xc9b08a, 0.24).setDepth(11);
      eventObjects.push(dust);
      this.scene.tweens.add({
        targets: dust,
        scaleX: 1.8,
        scaleY: 1.4,
        alpha: 0,
        duration: 700,
        delay,
      });
    };

    const makeLeaves = (x: number, y: number, delay: number) => {
      for (let i = 0; i < 4; i++) {
        const leaf = this.scene.add.ellipse(x, y, 9, 5, i % 2 === 0 ? 0x7ccf7a : 0x4f8d67, 0.76)
          .setDepth(11.5)
          .setAngle(i * 25);
        eventObjects.push(leaf);
        this.scene.tweens.add({
          targets: leaf,
          x: x - 20 - i * 8,
          y: y + 14 + i * 4,
          angle: leaf.angle - 90,
          alpha: 0,
          duration: 620,
          delay,
          ease: 'Quad.easeOut',
        });
      }
    };

    const treeX = ZONE2_POSITIONS.MONKEY_TREE.x;
    const monkeyConfigs = [
      { startX: treeX - 6, startY: ZONE2_POSITIONS.MONKEY_TREE.y + 36, endX: treeX - 24, endY: monkeyGroundY, scale: 0.95, delay: 0 },
      { startX: treeX + 22, startY: ZONE2_POSITIONS.MONKEY_TREE.y + 18, endX: treeX + 4, endY: monkeyGroundY + 2, scale: 0.88, delay: 160 },
      { startX: treeX - 38, startY: ZONE2_POSITIONS.MONKEY_TREE.y + 52, endX: treeX - 58, endY: monkeyGroundY + 4, scale: 0.8, delay: 280 },
    ];

    monkeyConfigs.forEach((cfg, idx) => {
      const monkey = this.scene.add.image(cfg.startX, cfg.startY, TEXTURE_KEYS.KERA)
        .setScale(cfg.scale)
        .setDepth(12 + idx)
        .setOrigin(0.5, 1)
        .setFlipX(true);
      eventObjects.push(monkey);

      this.scene.tweens.add({
        targets: monkey,
        x: cfg.endX,
        y: cfg.endY,
        duration: 540,
        delay: cfg.delay,
        ease: 'Cubic.easeIn',
      });
      makeLeaves(cfg.endX - 10, cfg.endY - 26, cfg.delay + 220);
      this.scene.tweens.add({
        targets: monkey,
        angle: idx % 2 === 0 ? -10 : 10,
        duration: 90,
        delay: cfg.delay,
        yoyo: true,
        repeat: 8,
      });
      this.scene.tweens.add({
        targets: monkey,
        x: cfg.endX - (120 + idx * 18),
        y: cfg.endY + 36 + idx * 8,
        duration: 840,
        delay: cfg.delay + 620,
        ease: 'Quad.easeOut',
      });
      makeDust(cfg.endX - (90 + idx * 14), cfg.endY + 40 + idx * 10, cfg.delay + 1040);
    });

    const tiger = this.scene.add.image(
      ZONE2_POSITIONS.BUSH_TIGER.x + 120,
      tigerGroundY - 8,
      TEXTURE_KEYS.HARIMAU,
    ).setScale(1.05).setDepth(15).setAlpha(0).setOrigin(0.5, 1).setFlipX(true);
    eventObjects.push(tiger);

    const tigerShadow = this.scene.add.ellipse(tiger.x, tigerGroundY + 8, 70, 18, 0x000000, 0.16).setDepth(11);
    eventObjects.push(tigerShadow);

    this.scene.cameras.main.shake(220, 0.0025);

    this.scene.tweens.add({
      targets: tiger,
      alpha: 1,
      x: ZONE2_POSITIONS.BUSH_TIGER.x - 120,
      y: tigerGroundY + 10,
      duration: 1260,
      delay: 540,
      ease: 'Cubic.easeOut',
    });
    this.scene.tweens.add({
      targets: tiger,
      angle: 5,
      duration: 110,
      yoyo: true,
      repeat: 10,
      delay: 540,
    });
    this.scene.tweens.add({
      targets: tigerShadow,
      x: ZONE2_POSITIONS.BUSH_TIGER.x - 110,
      width: 84,
      duration: 1300,
      delay: 540,
      ease: 'Quad.easeOut',
    });

    makeDust(ZONE2_POSITIONS.BUSH_TIGER.x - 18, tigerGroundY + 18, 980);
    makeDust(ZONE2_POSITIONS.BUSH_TIGER.x - 90, tigerGroundY + 22, 1220);
    makeLeaves(ZONE2_POSITIONS.BUSH_TIGER.x + 16, tigerGroundY - 20, 760);

    const holdMs = 900;
    const fadeMs = 450;
    const animEndMs = 3000;

    this.scene.time.delayedCall(animEndMs, () => {
      eventObjects.forEach((obj) => {
        this.scene.tweens.add({
          targets: obj,
          alpha: 0,
          duration: fadeMs,
          ease: 'Quad.easeOut',
        });
      });
    });

    this.scene.time.delayedCall(animEndMs + holdMs + fadeMs, () => {
      eventObjects.forEach((obj) => obj.destroy());
      this.playing = false;
      this.played = true;
      onComplete();
    });
  }
}
